import { Spacer, Text } from "@hackthenorth/north";
import { Button } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Accordion,
  AccordionArrow,
  AccordionHeadingContainer,
  errorToast,
  Header,
  PageWrapper,
} from "src/shared/components";
import { useGetHackerLazyQuery } from "src/shared/contexts/HackerContext/graphql/getHacker.generated";
import { QRCodeIcon, XMarkIcon } from "src/static/icons";

import ProfileCard from "../components/cards/ProfileCard";
import ScanModal from "../components/modals/organizer/hacker_scan/scan";
import ScanCameraModal from "../components/modals/organizer/hacker_scan/scan_camera";
import { getPalNumberFromFields } from "../hackPals";
import { GGPlayer } from "../types";

import { useGooseGamePlayerLazyQuery } from "./graphql/gooseGamePlayer.generated";
import PrizeCheckout from "./PrizeCheckout";
import QuestManagement from "./QuestManagement";
import RedemptionHistory from "./RedemptionHistory";

const GooseGamesHackers: React.FC = () => {
  const [getHackerData, { data: hackerData }] = useGetHackerLazyQuery();
  const [gooseGamePlayer, { data: playerData }] = useGooseGamePlayerLazyQuery();

  const [currentHacker, setCurrentHacker] = useState<null | GGPlayer>(null);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isScanCameraOpen, setIsScanCameraOpen] = useState(false);
  const [refreshHistory, setRefreshHistory] = useState(false);
  const [palNumber, setPalNumber] = useState<number | undefined>(undefined);

  useEffect(() => {
    setPalNumber(undefined);
    if (currentHacker && currentHacker.event_attendee) {
      try {
        getHackerData({
          variables: { myId: currentHacker.event_attendee.user.id },
        });
      } catch (error) {
        errorToast("Could not get hacker");
      }
    }
  }, [currentHacker, getHackerData]);

  useEffect(() => {
    if (hackerData) {
      setPalNumber(getPalNumberFromFields(hackerData.claims[0].fields));
    }
  }, [hackerData]);

  useEffect(() => {
    if (playerData?.gooseGamePlayer) {
      const ggPlayer = playerData.gooseGamePlayer;

      const player: GGPlayer = {
        event_attendee: {
          user: {
            id: ggPlayer.event_attendee.user.id,
            name: ggPlayer.event_attendee?.user?.name ?? "",
            email: ggPlayer.event_attendee?.user?.email ?? "",
          },
          badge_code: ggPlayer.event_attendee?.badge_code ?? null,
        },
        event_attendee_id: ggPlayer.event_attendee_id,
        current_points: ggPlayer.current_points,
        total_points: ggPlayer.total_points,
        created_at: ggPlayer.created_at,
      };
      setCurrentHacker(player);
    }
  }, [playerData]);

  return (
    <>
      <Header
        title="Goose Games"
        subtitle="Complete quests to collect points and redeem exclusive prizes!"
      />
      <StyledPageWrapper>
        <PageWrapper>
          <Flex>
            <Button
              leadingIcon={<QRCodeIcon width={16} color="white" />}
              size="md"
              color="primary"
              onClick={() => {
                setIsScanCameraOpen(true);
              }}
            >
              Scan Hacker
            </Button>
            {currentHacker && (
              <Button
                leadingIcon={<XMarkIcon width={10} />}
                size="md"
                color="secondary"
                onClick={() => {
                  setCurrentHacker(null);
                  setPalNumber(undefined);
                }}
              >
                Close Hacker
              </Button>
            )}
          </Flex>
          <Spacer height={36} />
          {currentHacker && currentHacker.event_attendee ? (
            <ProfileCard
              profile={{
                name: currentHacker.event_attendee.user.name,
                email: currentHacker.event_attendee.user.email,
              }}
              palNumber={palNumber}
              ggPlayer={currentHacker}
              refetchHacker={() =>
                gooseGamePlayer({
                  variables: {
                    event_attendee_id: currentHacker.event_attendee_id,
                  },
                })
              }
            />
          ) : isScanCameraOpen ? (
            <ScanCameraModal
              isOpen={isScanCameraOpen}
              onClose={() => setIsScanCameraOpen(false)}
              onManualInput={() => {
                setIsScanModalOpen(true);
                setIsScanCameraOpen(false);
              }}
              onScanResult={setCurrentHacker}
            />
          ) : (
            <>
              <MessageContainer>
                <StyledText type="subtitle">
                  No hacker selected - Scan hacker to begin.
                </StyledText>
              </MessageContainer>
              <ScanModal
                isOpen={isScanModalOpen}
                onConfirm={setCurrentHacker}
                onCancel={() => setIsScanModalOpen(false)}
              />
            </>
          )}
        </PageWrapper>

        {currentHacker && (
          <AccordianWrapper>
            <Accordion
              heading={
                <StyledHeadingContainer>
                  <Text mods="heading h3">Quest Management</Text>
                  <AccordionArrow />
                </StyledHeadingContainer>
              }
            >
              <QuestManagement GooseGamePlayer={currentHacker} />
            </Accordion>
            <Accordion
              heading={
                <StyledHeadingContainer>
                  <Text mods="heading h3">Prize Checkout</Text>
                  <AccordionArrow />
                </StyledHeadingContainer>
              }
            >
              <PrizeCheckout
                GooseGamePlayer={currentHacker}
                setRefreshHistory={setRefreshHistory}
              />
            </Accordion>
            <Accordion
              heading={
                <StyledHeadingContainer>
                  <Text mods="heading h3">Redemption History</Text>
                  <AccordionArrow />
                </StyledHeadingContainer>
              }
            >
              <RedemptionHistory
                GooseGamePlayer={currentHacker}
                refreshHistory={refreshHistory}
                setRefreshHistory={setRefreshHistory}
              />
            </Accordion>
          </AccordianWrapper>
        )}
      </StyledPageWrapper>
    </>
  );
};

const StyledPageWrapper = styled.div`
  padding: 0;
`;
const AccordianWrapper = styled.div`
  width: 100%;
`;
const StyledHeadingContainer = styled(AccordionHeadingContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 36px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const MessageContainer = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  padding: 24px 36px;
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary} !important;
  font-weight: 500 !important;
`;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
`;

export default GooseGamesHackers;
